/* eslint-disable camelcase */
import React, { useCallback, useEffect, useState } from 'react';
import FormContents from 'components/atoms/Form/FormContents';
import FlexBox from 'components/atoms/FlexBox';
import FlexBoxItem from 'components/atoms/FlexBoxItem';
import BlockUI from 'components/molecules/BlockUi';
import TabControl from 'components/atoms/TabControl';
import FormLabel from 'components/atoms/Form/FormLabel';
import useCurrentRoleScreen from 'hooks/useCurrentRoleScreen';
import { PrimaryButton } from 'components/atoms/Button';
import { exportFilePDF } from 'api/salesReport';
import DatePickerForm from 'components/molecules/DatePickerForm';
import OrganizationFilteredTree from 'components/organismos/organization/OrganizationFilteredTree/OrganizationFilteredTree';
import moment from 'moment';
import BodyText from 'components/atoms/BodyText';
import useToastNotification from 'hooks/useToastNotification';
import { css } from '@emotion/core';
import useSalesMonthlyReport from './hooks';
import SalesReportTable from './SalesReportTable';
import HourlyReport from './HourlyReport';
import CashRegisterReport from './CashRegisterReport';
import useApplicationReport, { downloadOriginalPDF } from './hookApplicationReport';
import { getOrgTargetDates } from 'utility/orgTargetDateUtil';

type SelectType = 'dailyReport' | 'cashRegisterReport' | 'hourlyReport' | 'none';
const SelectType: { [key in SelectType]: SelectType } = {
  dailyReport: 'dailyReport',
  cashRegisterReport: 'cashRegisterReport',
  hourlyReport: 'hourlyReport',
  none: 'none',
};
const SalesDailyReportLayout = () => {
  const {
    targetOrgCode,
    setTargetOrgCode,
    countAdvertising,
    generalData,
    dataKeyName,
    grandTotalnData,
    sameDayData,
    advertisingData,
    isLoading,
    setIsLoading,
    targetDay,
    setTargetDay,
    cashRegisterReport,
    reportByTimeOfDay,
    setGeneralData,
    updateCountDayAndAdvertising,
    budgetProgressComment,
    storeOperationComment,
    otherComment,
    setBudgetProgressComment,
    setStoreOperationComment,
    setOtherComment,
    paymentMediaAmountManual,
    setPaymentMediaAmountManual,
    giftAmountManual,
    setGiftAmountManual,
    attachFileData,
    setAttachFileData,
    discountTypeData,
    setDiscountTypeData,
    setting,
    detailApplicationData,
    expensesData,
    countExpenses,
  } = useSalesMonthlyReport();
  const [selectType, setSelectType] = React.useState<SelectType>(SelectType.none);
  const [categoryArr, setCategoryArr] = useState<Array<String>>([]);
  const staffCode = sessionStorage.getItem('loginUser.staffCode') || '';
  const [reportStatus, setReportStatus] = useState<number | null>(null);
  const [isStatusVisible, setIsStatusVisible] = useState<boolean>(false);
  const [salesBudgetPlanDailyStatus, setSalesBudgetPlanDailyStatus] = useState<number | null>(null);
  const { errorNotification } = useToastNotification();
  const [orgName, setOrgName] = useState(
    sessionStorage.getItem('salesMonthlyReport.orgName') || sessionStorage.getItem('loginUser.orgName'),
  );
  const handleClick = useCallback(
    () => {
      return exportFilePDF(targetOrgCode, targetDay)
        .then((response: any) => {
          setIsLoading(false);
          downloadOriginalPDF(response, 'レポート（日次）.pdf');
        })
        .catch((error) => {
          setIsLoading(false);
          errorNotification('対象月、対象店舗において在籍するスタッフがいませんでした。');
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetOrgCode, targetDay],
  );

  const getStatus = (status: number | null, budgetStatus: number | null = null) => {
    setReportStatus(status);
    if (budgetStatus !== undefined) setSalesBudgetPlanDailyStatus(budgetStatus);
  };

  const printStatus = (): String => {
    switch (reportStatus) {
      case 0:
        return '申請中';
      case 1:
        return '差し戻し';
      case 2:
        return '承認';
      default:
        // null
        if (salesBudgetPlanDailyStatus === null) return '-';
        if (salesBudgetPlanDailyStatus === 3) return '-';
        return '未申請'; // salesBudgetPlanDailyStatus !== 3
    }
  };
  const printColor = (): 'red' | 'green' | 'blue' | 'black' => {
    switch (reportStatus) {
      case 0:
        return 'blue';
      case 1:
        return 'red';
      case 2:
        return 'green';
      default:
        return salesBudgetPlanDailyStatus === null || salesBudgetPlanDailyStatus === 3 ? 'black' : 'red';
    }
  };

  useApplicationReport(targetDay, targetOrgCode, getStatus, setIsStatusVisible);

  const roleScreen = useCurrentRoleScreen('/salesDailyReport');

  const staffAccess =
    roleScreen &&
    roleScreen.useStaffAccess === true &&
    (roleScreen.staffAccessFunctionRoles || []).filter((role: any) => role.orgCode === targetOrgCode);

  const checkUserRole = staffAccess && staffAccess.length > 0 ? staffAccess[0] : roleScreen;

  // 集計用の日付を計算
  const getOrgTargetDays = useCallback(() => {
    if (!targetDay) {
      return { from: new Date(), to: new Date() };
    }
    return getOrgTargetDates(targetDay, targetDay);
  }, [targetDay]);

  const orgTargetDates = getOrgTargetDays();
  const orgTargetDayFrom = orgTargetDates.from;
  const orgTargetDayTo = orgTargetDates.to;

  useEffect(() => {
    if (checkUserRole && checkUserRole.available) {
      setSelectType(
        checkUserRole.usabilityGeneralItem5 === 1 ? SelectType.dailyReport : SelectType.cashRegisterReport,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkUserRole]);

  return (
    <div>
      <FormContents>
        <FlexBox>
          <FlexBoxItem basis="8%">
            <FormLabel label="営業日：" />
          </FlexBoxItem>
          <FlexBoxItem basis="1%" />

          <FlexBoxItem basis="20%">
            <DatePickerForm
              dateFormat="yyyy年MM月dd日"
              label=""
              isFullWidth={true}
              date={targetDay}
              changeDate={(date: Date) => {
                if (date !== null) {
                  setTargetDay(date);
                  sessionStorage.setItem('SalesDailyReport.targetDay', moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD'));
                }
              }}
            />
          </FlexBoxItem>
          {checkUserRole && checkUserRole.downloadFlag === 1 && selectType === SelectType.cashRegisterReport && (
            <FlexBoxItem basis="70%" customStyle={css({ textAlign: 'right' })}>
              <PrimaryButton
                ghost={false}
                text="印刷"
                onClick={() => {
                  setIsLoading(true);
                  handleClick();
                }}
              />
            </FlexBoxItem>
          )}
        </FlexBox>

        <OrganizationFilteredTree
          functionType={3}
          orgCallback={(args: string | Array<string>) => {
            setTargetOrgCode(String(args));
            sessionStorage.setItem('SalesDailyReport.targetOrgCode', String(args));
          }}
          targetdayForm={orgTargetDayFrom}
          targetdayTo={orgTargetDayTo}
          initOrgValue={String(targetOrgCode)}
          staffCode={staffCode}
          orgLabel="店舗名"
          addAllItem={false}
          orgCategoryCallback={(args: Array<string>) => {
            setCategoryArr(args);
          }}
          orgNameCallback={(args: string) => {
            setOrgName(args);
          }}
          apiVersion="v6"
          menuId={roleScreen?.menuId}
        />
        {isStatusVisible && (
          <BodyText size="re">
            レポート状況：
            <strong style={{ marginLeft: 60, color: printColor() }}>{printStatus()}</strong>
          </BodyText>
        )}
        <div style={{ marginTop: '20px' }}>
          <BlockUI blocking={isLoading}>
            <TabControl
              items={[
                {
                  value: SelectType.dailyReport,
                  display: ' 進捗状況 ',
                  disabled: !(checkUserRole && checkUserRole.usabilityGeneralItem5 === 1),
                },
                {
                  value: SelectType.cashRegisterReport,
                  display: ' レジレポート',
                },
                {
                  value: SelectType.hourlyReport,
                  display: ' 入店時間別レポート ',
                },
                {
                  value: SelectType.none,
                  display: ' ',
                  disabled: true,
                },
              ]}
              setValue={setSelectType as (arg: string) => void}
              currentValue={selectType}
              isFixed={true}
            />
            <div style={{ marginTop: '20px' }}>
              {/* 進捗状況 */}
              {selectType === SelectType.dailyReport && (
                <SalesReportTable
                  grandTotalnData={grandTotalnData}
                  sameDayData={sameDayData}
                  generalData={generalData}
                  dataKeyName={dataKeyName}
                  countAdvertising={countAdvertising}
                  advertisingData={advertisingData}
                  targetDay={targetDay}
                  setting={setting || []}
                  getStatus={getStatus}
                  targetOrgCode={targetOrgCode}
                  countExpenses={countExpenses}
                  expensesData={expensesData}
                />
              )}
              {/* レジレポート */}
              {selectType === SelectType.cashRegisterReport && (
                <CashRegisterReport
                  dailyData={generalData}
                  cashRegisterReport={cashRegisterReport}
                  setIsLoading={setIsLoading}
                  orgName={orgName}
                  targetOrgCode={targetOrgCode}
                  targetDay={targetDay}
                  setGeneralData={setGeneralData}
                  updateCountDayAndAdvertising={updateCountDayAndAdvertising}
                  budgetProgressComment={budgetProgressComment}
                  storeOperationComment={storeOperationComment}
                  otherComment={otherComment}
                  setBudgetProgressComment={setBudgetProgressComment}
                  setStoreOperationComment={setStoreOperationComment}
                  setOtherComment={setOtherComment}
                  paymentMediaAmountManual={paymentMediaAmountManual}
                  setPaymentMediaAmountManual={setPaymentMediaAmountManual}
                  giftAmountManual={giftAmountManual}
                  setGiftAmountManual={setGiftAmountManual}
                  attachFileData={attachFileData}
                  setAttachFileData={setAttachFileData}
                  discountTypeData={discountTypeData}
                  setDiscountTypeData={setDiscountTypeData}
                  getStatus={getStatus}
                  sameDayData={sameDayData}
                  setting={setting || []}
                  detailApplicationData={detailApplicationData}
                  selectType={selectType}
                  roleScreen={checkUserRole}
                />
              )}

              {/* 入店時間別 */}
              {selectType === SelectType.hourlyReport && (
                <HourlyReport
                  reportByTimeOfDay={reportByTimeOfDay}
                  getStatus={getStatus}
                  targetOrgCode={targetOrgCode}
                  targetDay={targetDay}
                />
              )}
            </div>
          </BlockUI>
        </div>
      </FormContents>
    </div>
  );
};
export default SalesDailyReportLayout;
